import { useEffect, useMemo, useState } from 'react';

import { convertExtractedAnnotations, onlyScoredAnnotationsByType } from 'src/data/annotation';
import { type AssetType, isAssetDisabledAnnotations } from 'src/data/asset';
import type { DocumentVariation, DocumentVariationDetails } from 'src/data/document';
import { GENERATION_GUIDANCE_TYPE } from 'src/data/generation';
import { takeTopAudiences } from 'src/data/insights';
import { calculateBlockedReason } from 'src/data/utils';
import { parseHistogramData, type Variation } from 'src/data/variation';
import { captureMessage } from 'src/lib/sentry';

export type FetchDocumentVariationDetails = (
  variation: DocumentVariation,
  text: string
) => Promise<DocumentVariationDetails | null | undefined>;

interface Params {
  variation: DocumentVariation;
  text: string;
  fetchDetails: FetchDocumentVariationDetails;
}

export const useDetailsData = ({ variation, text, fetchDetails }: Params) => {
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState<DocumentVariationDetails | null>(null);

  useEffect(() => {
    let current = true;

    const fetch = async () => {
      setLoading(true);
      try {
        const result = await fetchDetails(variation, text);
        if (current) {
          setResultData(result ?? null);
        }
      } catch (error) {
        captureMessage('Failed to get the details of a saved item', {
          level: 'warning',
          extra: { error, source: 'ReadonlyDocumentScores -> useDetailsData' },
        });
      } finally {
        if (current) {
          setLoading(false);
        }
      }
    };

    fetch();
    return () => {
      current = false;
    };
    // A new details request is needed for another item or other text only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variation.id, text, fetchDetails]);

  const scoreData = useMemo(
    () => ({
      score: resultData?.scoring?.score,
      basedOn: resultData?.scoring?.engagementScoreSupportSize,
      loading,
    }),
    [resultData, loading]
  );

  const histogramsData = useMemo(() => {
    const histograms = parseHistogramData(resultData?.scoring as Variation);
    return { age: histograms?.ages, gender: histograms?.genders, loading };
  }, [resultData, loading]);

  const blockedReason = useMemo(() => calculateBlockedReason(text), [text]);

  const annotationsData = useMemo(() => {
    const annotations = isAssetDisabledAnnotations(variation.assetType as AssetType)
      ? []
      : onlyScoredAnnotationsByType(
          convertExtractedAnnotations(resultData?.annotations ?? {}),
          GENERATION_GUIDANCE_TYPE.feature
        );
    return { annotations, loading };
  }, [loading, resultData?.annotations, variation.assetType]);

  const sensitivityData = useMemo(
    () => ({ sensitivity: resultData?.validations, loading }),
    [resultData, loading]
  );

  const audienceData = useMemo(
    () => ({ data: takeTopAudiences(resultData?.targetAudienceInsights), loading }),
    [resultData, loading]
  );

  return {
    scoreData,
    histogramsData,
    blockedReason,
    annotationsData,
    sensitivityData,
    audienceData,
  };
};
